'use client';

import {
  useEffect,
  useMemo,
  useId,
  Fragment,
  type FC,
  type ChangeEvent,
} from 'react';
import styles from './Select.module.css';
import { Icon, Flex, Text, Loading } from '@/_components';
import { getClasses } from '@/_utils/classnames';
import type { SelectItem, SelectProps } from './Select.types';

export const Select: FC<SelectProps> = ({
  iconHelperText,
  helperText,
  helperTextColor,
  isDisabled,
  className,
  isFullWidth,
  isLabelHidden,
  isLoading,
  isRequired,
  items,
  label,
  onChange,
  placeholder,
  selectedValue = '',
  shouldAutoSelectIfSingle,
  id,
}) => {
  const generatedId: string = useId();
  const htmlFor: string = id || generatedId;

  const selectClasses: string = getClasses(styles.select, className, {
    [styles.selectFullWidth]: isFullWidth,
    [styles.selectDisabled]: isDisabled,
    [styles.selectHiddenLabel]: !label || isLabelHidden,
  });

  const showPlaceholder: boolean = !isLoading && !selectedValue;

  const selectableOptions: Array<SelectItem> = useMemo(
    () => items?.filter((item) => !!item.value) || [],
    [items],
  );

  const hasSingleSelectableOption: boolean = selectableOptions.length === 1;

  const firstItemValue: string | undefined = useMemo(
    () => selectableOptions?.[0]?.value,
    [selectableOptions],
  );

  const isEmptySelect: boolean = items?.length === 0 && !isLoading;
  const isSingleItemSelect: boolean =
    !!shouldAutoSelectIfSingle &&
    hasSingleSelectableOption &&
    !isLoading &&
    !!firstItemValue;
  const isSelectDisabled: boolean = Boolean(
    isDisabled ||
      isEmptySelect ||
      (shouldAutoSelectIfSingle && hasSingleSelectableOption),
  );

  const selectContainerClasses: string = getClasses(
    styles.select__select__selectContainer,
  );

  const selectLoadingIconClasses: string = getClasses(
    styles.select__icon,
    styles.select__iconLoading,
  );

  useEffect(() => {
    if (isSingleItemSelect) {
      const syntheticEvent = {
        target: { value: firstItemValue },
      } as ChangeEvent<HTMLSelectElement>;
      onChange(syntheticEvent);
    }
  }, [isSingleItemSelect, firstItemValue, onChange]);

  return (
    <Flex isColumn={true} className={selectClasses}>
      {!isLabelHidden && (
        <label htmlFor={htmlFor} className="flex gap-2 flex-y-center">
          <Text
            type="small"
            fontWeight="medium"
            color={isSelectDisabled ? 'secondary' : 'primary'}
          >
            {label}
          </Text>
          {isRequired && (
            <Text type="small" color="critical">
              *
            </Text>
          )}
        </label>
      )}
      <Flex className={selectContainerClasses}>
        {isLoading ? (
          <Loading size={16} className={selectLoadingIconClasses} />
        ) : (
          <Icon size={16} name="caret-down" className={styles.select__icon} />
        )}
        {showPlaceholder && (
          <Text
            className={styles.select__placeholder}
            type="small"
            color="disabled"
          >
            {placeholder}
          </Text>
        )}
        <select
          aria-label={label}
          aria-disabled={isSelectDisabled}
          aria-required={isRequired}
          className={styles.select__select}
          onChange={onChange}
          id={htmlFor}
          value={selectedValue}
          disabled={isSelectDisabled}
          required={isRequired}
        >
          {showPlaceholder && (
            <option value="" disabled={true} hidden={true}></option>
          )}
          {items?.map((item, key) => {
            const isPlaceholder: boolean = item.value === '';

            return (
              <Fragment key={key}>
                {isPlaceholder && <option style={{ display: 'none' }}></option>}
                <option value={item.value as string}>{item.name}</option>
              </Fragment>
            );
          })}
        </select>
      </Flex>
      {helperText && (
        <Flex gap={2} isYCentered={true}>
          {iconHelperText && (
            <Icon name={iconHelperText} size={16} color="disabled" />
          )}
          <Text type="tiny" color={helperTextColor ?? 'secondary'}>
            {helperText}
          </Text>
        </Flex>
      )}
    </Flex>
  );
};
